import { orderEventLabel, translateMessage, t } from "../i18n";
import { BarChart3, ChevronDown, ChevronUp, Printer, Search, Trash2, X } from "lucide-react";
import { useMemo, useState } from "react";
import { PrinterSplitButton } from "../components/PrinterSplitButton";
import { useBackHandler } from "../backNavigation";
import { loadHistoryExpansionMode } from "../storage";
import type { OrderEvent, OrderRecord, Orders, PrinterConfig, SalesPeriodReport } from "../types";
import {
  effectiveOrderLines,
  formatDateTime,
  formatMoney,
  normalize,
  orderEvents,
  orderLinesForEvent,
  orderUpdatedAt,
  optionLabels
} from "../utils";

type Props = {
  orders: Orders | null;
  printers: PrinterConfig[];
  canReprint: boolean;
  canClear: boolean;
  busy: boolean;
  onReprint: (order: OrderRecord, printerIds: number[]) => Promise<void>;
  onClear: () => Promise<void>;
  onLoadReport: () => Promise<SalesPeriodReport>;
  onPrintReport: (report: SalesPeriodReport, printerId: number) => Promise<void>;
};

function lastEventId(order: OrderRecord, events: OrderEvent[]): number {
  return events.reduce(
    (highest, event) => (event.order_id === order.id && event.id > highest ? event.id : highest),
    0
  );
}

function lineDetails(line: { without_options: string; extras: string; counters: string; comment: string; takeaway: number }): string[] {
  return [
    line.without_options ? t("Χωρίς: {0}", line.without_options) : "",
    line.extras ? `Extra: ${optionLabels(line.extras).join(", ")}` : "",
    line.counters || "",
    line.comment ? t("Σχόλιο: {0}", line.comment) : "",
    line.takeaway ? t("Πακέτο") : ""
  ].filter(Boolean);
}

export function HistoryScreen({
  orders,
  printers,
  canReprint,
  canClear,
  busy,
  onReprint,
  onClear,
  onLoadReport,
  onPrintReport
}: Props) {
  const [query, setQuery] = useState("");
  const defaultExpanded = loadHistoryExpansionMode() === "open";
  const [expansionOverrides, setExpansionOverrides] = useState<Record<number, boolean>>({});
  const [printingId, setPrintingId] = useState<number | null>(null);
  const [confirmClear, setConfirmClear] = useState(false);
  const [report, setReport] = useState<SalesPeriodReport | null>(null);
  const [reportOpen, setReportOpen] = useState(false);
  useBackHandler(reportOpen, () => setReportOpen(false));
  useBackHandler(confirmClear, () => setConfirmClear(false));
  const [reportLoading, setReportLoading] = useState(false);
  const [reportError, setReportError] = useState("");
  const [reportPrinterId, setReportPrinterId] = useState(printers[0]?.id ?? 0);
  const events = orders?.order_events ?? [];
  const rows = useMemo(() => {
    const needle = normalize(query);
    return (orders?.orders ?? [])
      .filter((order) => {
        if (!needle) {
          return true;
        }
        return normalize(`${order.table_label} ${order.order_number} ${order.waiter}`).includes(needle);
      })
      // Same ordering as the Android query: newest activity first, then the newest event id and
      // finally the order id, so two orders stamped in the same millisecond cannot swap places.
      .sort((a, b) =>
        orderUpdatedAt(b, events) - orderUpdatedAt(a, events) ||
        lastEventId(b, events) - lastEventId(a, events) ||
        b.id - a.id
      );
  }, [events, orders, query]);

  const print = async (order: OrderRecord, printerIds: number[]): Promise<void> => {
    setPrintingId(order.id);
    try {
      await onReprint(order, printerIds);
    } finally {
      setPrintingId(null);
    }
  };

  const openReport = async (): Promise<void> => {
    setReportOpen(true);
    setReportLoading(true);
    setReportError("");
    if (!printers.some((printer) => printer.id === reportPrinterId)) {
      setReportPrinterId(printers[0]?.id ?? 0);
    }
    try {
      setReport(await onLoadReport());
    } catch (error) {
      setReportError(error instanceof Error ? error.message : t("Η αναφορά δεν φορτώθηκε."));
    } finally {
      setReportLoading(false);
    }
  };

  return (
    <section className="screen history-screen" aria-labelledby="history-heading">
      <div className="screen-heading-row">
        <div>
          <h1 id="history-heading">{t("Ιστορικό")}</h1>
          <p>{rows.length}{t(" παραγγελίες")}</p>
        </div>
        {canClear ? (
          <div className="history-admin-actions">
            <button type="button" className="secondary-command" onClick={() => void openReport()} disabled={busy || !rows.length}>
              <BarChart3 />
              <span>{t("Αναφορά περιόδου")}</span>
            </button>
            {rows.length ? (
              <button type="button" className="secondary-command danger-command" onClick={() => setConfirmClear(true)} disabled={busy}>
                <Trash2 />
                <span>{t("Καθαρισμός ιστορικού")}</span>
              </button>
            ) : null}
          </div>
        ) : null}
      </div>
      <label className="search-field history-search">
        <Search />
        <input
          type="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={t("Τραπέζι, κωδικός ή σερβιτόρος")}
          aria-label={t("Αναζήτηση ιστορικού")}
        />
        {query ? (
          <button type="button" className="icon-button" onClick={() => setQuery("")} aria-label={t("Καθαρισμός")}>
            <X />
          </button>
        ) : null}
      </label>

      <div className="history-list">
        {rows.map((order) => {
          const lines = effectiveOrderLines(orders, order.id);
          const events = orderEvents(orders, order.id);
          const expanded = expansionOverrides[order.id] ?? defaultExpanded;
          const total = lines.reduce((sum, line) => sum + line.price * line.quantity, 0);
          const waiterCounts = new Map<string, number>();
          events.slice().sort((a, b) => a.created_at - b.created_at).forEach((event) => {
            const quantity = orderLinesForEvent(orders, event.id)
              .reduce((sum, line) => sum + Math.max(0, line.quantity), 0);
            if (quantity > 0) {
              const waiter = event.waiter || order.waiter;
              waiterCounts.set(waiter, (waiterCounts.get(waiter) ?? 0) + quantity);
            }
          });
          const finalQuantity = lines.reduce((sum, line) => sum + line.quantity, 0);
          const waiterSummary = waiterCounts.size
            ? Array.from(waiterCounts, ([waiter, quantity]) => `${waiter}: ${quantity}`).join(" · ")
            : `${order.waiter}: ${finalQuantity}`;
          return (
            <article className="history-card" key={order.id}>
              <button
                type="button"
                className="history-card-heading"
                onClick={() => setExpansionOverrides((current) => ({
                  ...current,
                  [order.id]: !expanded
                }))}
                aria-expanded={expanded}
              >
                <span>
                  <strong>{t("Τραπέζι ")}{order.table_label}</strong>
                  <b>#{order.order_number}</b>
                </span>
                <span className="history-meta">
                  <small>{formatDateTime(orderUpdatedAt(order, events))}</small>
                  {expanded ? <ChevronUp /> : <ChevronDown />}
                </span>
              </button>
              <div className="history-summary">
                <span>{finalQuantity}{t(" είδη")}</span>
                <span className="history-waiters" title={waiterSummary}>{waiterSummary}</span>
                <strong>{formatMoney(total)}</strong>
              </div>
              {expanded ? (
                <div className="history-details">
                  <section className="history-final-order">
                    <h3>{t("Τελική παραγγελία")}</h3>
                    <div className="history-lines">
                      {lines.map((line) => (
                        <div className="history-line detailed" key={`${line.id}-${line.name}`}>
                          <strong>{line.quantity}x</strong>
                          <span>
                            <b>{line.name}</b>
                            {lineDetails(line).map((detail) => <small key={detail}>{detail}</small>)}
                          </span>
                          <b>{formatMoney(line.price * line.quantity)}</b>
                        </div>
                      ))}
                    </div>
                  </section>
                  {events.length ? (
                    <section className="history-events">
                      <h3>{t("Ενημερώσεις")}</h3>
                      {events.map((event) => {
                        const eventLines = orderLinesForEvent(orders, event.id);
                        return (
                          <article className="history-event" key={event.id}>
                            <header>
                              <strong>{orderEventLabel(event.type)}</strong>
                              <small>{formatDateTime(event.created_at)} · {event.waiter}</small>
                            </header>
                            {event.note ? <p>{event.note}</p> : null}
                            {eventLines.map((line) => (
                              <div className="history-event-line" key={line.id}>
                                <strong>{line.quantity > 0 ? "+" : ""}{line.quantity}x</strong>
                                <span>{line.name}</span>
                              </div>
                            ))}
                          </article>
                        );
                      })}
                    </section>
                  ) : null}
                  {canReprint ? (
                    <div className="history-print">
                      <PrinterSplitButton
                        label={t("Επανεκτύπωση")}
                        printers={printers}
                        busy={busy || printingId === order.id}
                        onPrint={(printerIds) => void print(order, printerIds)}
                      />
                    </div>
                  ) : null}
                </div>
              ) : null}
            </article>
          );
        })}
      </div>
      {!rows.length ? <div className="empty-state">{t("Δεν βρέθηκαν παραγγελίες.")}</div> : null}

      {reportOpen ? (
        <div className="dialog-backdrop">
          <section className="details-dialog sales-report-dialog" role="dialog" aria-modal="true" aria-label={t("Αναφορά περιόδου")}>
            <header className="dialog-header">
              <div><h2>{t("Αναφορά περιόδου")}</h2><p>{t("Όλο το ιστορικό από τον τελευταίο καθαρισμό")}</p></div>
              <button type="button" className="icon-button" onClick={() => setReportOpen(false)} aria-label={t("Κλείσιμο")}><X /></button>
            </header>
            <div className="dialog-scroll sales-report-preview">
              {reportLoading ? <p>{t("Υπολογισμός αναφοράς...")}</p> : null}
              {reportError ? <p className="error-text">{translateMessage(reportError)}</p> : null}
              {report ? (
                <>
                  <div className="report-summary-grid">
                    <span><small>{t("Παραγγελίες")}</small><strong>{report.order_count}</strong></span>
                    <span><small>{t("Είδη")}</small><strong>{report.item_quantity}</strong></span>
                    <span><small>{t("Πωλήσεις")}</small><strong>{formatMoney(report.total)}</strong></span>
                  </div>
                  <p className="hint-text">{t("Έναρξη: ")}{formatDateTime(report.started_at || report.first_order_at)}</p>
                  {report.categories.map((category) => (
                    <section className="report-section" key={`${category.category_id}-${category.name}`}>
                      <header><strong>{category.name}</strong><b>{category.quantity} · {formatMoney(category.total)}</b></header>
                      {category.products.map((product) => (
                        <div className="report-row" key={`${product.product_id}-${product.name}`}><span>{product.quantity}x {product.name}</span><strong>{formatMoney(product.total)}</strong></div>
                      ))}
                    </section>
                  ))}
                  {report.extras.length ? (
                    <section className="report-section"><header><strong>{t("Πρόσθετα")}</strong></header>{report.extras.map((extra) => <div className="report-row" key={extra.name}><span>{extra.quantity}x {extra.name}</span><strong>{extra.total ? formatMoney(extra.total) : t("Δωρεάν")}</strong></div>)}</section>
                  ) : null}
                  {report.withouts.length ? (
                    <section className="report-section"><header><strong>{t("Χωρίς")}</strong></header>{report.withouts.map((item) => <div className="report-row" key={`${item.product_id}-${item.name}`}><span>{item.quantity}x {item.product_name}: {item.name}</span></div>)}</section>
                  ) : null}
                </>
              ) : null}
            </div>
            {report ? (
              <div className="sales-report-printer">
                <label className="field"><span>{t("Εκτυπωτής")}</span><select value={reportPrinterId} onChange={(event) => setReportPrinterId(Number(event.target.value))}>{printers.map((printer) => <option value={printer.id} key={printer.id}>{printer.nickname || printer.device_name}</option>)}</select></label>
              </div>
            ) : null}
            <footer className="dialog-actions">
              <button type="button" className="secondary-command" onClick={() => setReportOpen(false)}><span>{t("Κλείσιμο")}</span></button>
              <button type="button" className="primary-command" disabled={busy || reportLoading || !report || report.empty || !reportPrinterId} onClick={() => report && void onPrintReport(report, reportPrinterId)}><Printer /><span>{t("Εκτύπωση")}</span></button>
            </footer>
          </section>
        </div>
      ) : null}

      {confirmClear ? (
        <div className="dialog-backdrop">
          <section className="permission-dialog" role="dialog" aria-modal="true" aria-label={t("Διαγραφή ιστορικού")}>
            <header>
              <h2>{t("Διαγραφή ιστορικού")}</h2>
              <p>{t("Η τρέχουσα περίοδος θα αρχειοθετηθεί και το ορατό ιστορικό θα ξεκινήσει από την αρχή.")}</p>
            </header>
            <div className="dialog-actions">
              <button type="button" className="secondary-command" onClick={() => setConfirmClear(false)} disabled={busy}>
                <span>{t("Άκυρο")}</span>
              </button>
              <button
                type="button"
                className="primary-command danger-command"
                disabled={busy}
                onClick={async () => {
                  await onClear();
                  setConfirmClear(false);
                }}
              >
                <Trash2 />
                <span>{t("Διαγραφή")}</span>
              </button>
            </div>
          </section>
        </div>
      ) : null}
    </section>
  );
}
